import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Webcam from 'react-webcam';
import { format } from 'date-fns';
import { useAuthStore } from '../../../store/authStore';
import {
    saveOfflinePhoto,
    getOfflinePhotos,
    addPendingAction,
} from '../../../utils/offlineDb';
import { PhotoMetadata } from './types';
import { API_BASE } from '../../../utils/api';

interface UsePhotoCaptureProps {
    reportId: number;
    onPhotoTaken: (file: File, taskItem: string) => Promise<void>;
    onBack: () => void;
}

export function usePhotoCapture({
    reportId,
    onPhotoTaken,
    onBack,
}: UsePhotoCaptureProps) {
    const [selectedTask, setSelectedTask] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [photos, setPhotos] = useState<{ url: string; file: File }[]>([]);
    const [offlinePhotos, setOfflinePhotos] = useState<any[]>([]);
    const [showCamera, setShowCamera] = useState(false);
    const [facingMode] = useState<'environment' | 'user'>('environment');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [flashMode, setFlashMode] = useState<'auto' | 'on' | 'off'>('off');
    const [hasFlash, setHasFlash] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [lastPhoto, setLastPhoto] = useState<{
        url: string;
        timestamp: number;
    } | null>(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [metadata, setMetadata] = useState<PhotoMetadata>({
        latitude: 0,
        longitude: 0,
        accuracy: 0,
        timestamp: Date.now(),
    });

    const { accessToken } = useAuthStore();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const webcamRef = useRef<Webcam>(null);
    const trackRef = useRef<MediaStreamTrack | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    // Initialize camera and check available devices
    const initializeCamera = useCallback(async () => {
        try {
            // Try environment camera first with fallback to user camera
            const constraints = {
                video: {
                    facingMode: { ideal: 'environment' },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
            };

            const stream =
                await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            const track = stream.getVideoTracks()[0];
            trackRef.current = track;

            // Check if flash is available
            const capabilities = track.getCapabilities();
            setHasFlash(!!capabilities.torch);

            // Turn off flash initially
            if (capabilities.torch) {
                await track.applyConstraints({
                    advanced: [{ torch: false }],
                });
            }
        } catch (error: any) {
            console.error('Failed to initialize camera:', error);
            if (
                error instanceof DOMException &&
                error.name === 'NotAllowedError'
            ) {
                handleCameraError(
                    new Error(
                        'Camera access denied. Please enable camera permissions.',
                    ),
                );
            } else {
                handleCameraError(error);
            }
        }

        // Add fullscreen change listener
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setIsFullscreen(false);
                if (screen.orientation && screen.orientation.unlock) {
                    screen.orientation.unlock();
                }
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () =>
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange,
            );
    }, []);

    useEffect(() => {
        if (showCamera) {
            initializeCamera();
        } else {
            // Clean up camera resources when hiding camera
            if (trackRef.current) {
                trackRef.current.stop();
                trackRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        }
    }, [showCamera, initializeCamera]);

    // Clean up camera resources on unmount
    useEffect(() => {
        return () => {
            if (trackRef.current) {
                trackRef.current.stop();
                trackRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };
    }, []);

    const loadOfflinePhotos = async () => {
        try {
            const photos = await getOfflinePhotos(reportId);
            setOfflinePhotos(photos);
        } catch (error) {
            console.error('Error loading offline photos:', error);
        }
    };

    const updateLocation = async () => {
        setIsGettingLocation(true);
        setLocationError(null);
        try {
            // Check if geolocation is supported
            if (!navigator.geolocation) {
                throw new Error('Geolocation is not supported by your device');
            }

            // Try to get location with a more robust approach
            const position = await new Promise<GeolocationPosition>(
                (resolve, reject) => {
                    let attempts = 0;
                    const maxAttempts = 3;
                    const tryGetLocation = () => {
                        // First try with high accuracy
                        const options = {
                            enableHighAccuracy: attempts === 0, // Only use high accuracy on first attempt
                            timeout: 10000, // Increased timeout
                            maximumAge: attempts === 0 ? 0 : 30000, // Allow cached positions on retries
                        };

                        navigator.geolocation.getCurrentPosition(
                            (position) => {
                                resolve(position);
                            },
                            (error) => {
                                attempts++;
                                if (attempts < maxAttempts) {
                                    console.log(
                                        `Location attempt ${attempts} failed, retrying...`,
                                    );
                                    setTimeout(tryGetLocation, 1000); // Wait 1 second before retry
                                } else {
                                    let errorMessage = 'Could not get location';
                                    switch (error.code) {
                                        case error.PERMISSION_DENIED:
                                            errorMessage =
                                                'Location access denied. Please enable location services.';
                                            break;
                                        case error.POSITION_UNAVAILABLE:
                                            errorMessage =
                                                'Location information unavailable.';
                                            break;
                                        case error.TIMEOUT:
                                            errorMessage =
                                                'Location request timed out.';
                                            break;
                                    }
                                    reject(new Error(errorMessage));
                                }
                            },
                            options,
                        );
                    };

                    tryGetLocation();
                },
            );

            // Validate coordinates
            if (
                !position.coords ||
                !isFinite(position.coords.latitude) ||
                !isFinite(position.coords.longitude)
            ) {
                throw new Error('Invalid location data received');
            }

            // Update metadata with valid coordinates
            setLocationError(null);
            setMetadata({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: Date.now(),
                deviceInfo: {
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                    vendor: navigator.vendor,
                },
            });

            // Start watching position for updates if camera is active
            if (showCamera) {
                const watchId = navigator.geolocation.watchPosition(
                    (watchPosition) => {
                        setMetadata((prev) => ({
                            ...prev,
                            latitude: watchPosition.coords.latitude,
                            longitude: watchPosition.coords.longitude,
                            accuracy: watchPosition.coords.accuracy,
                            timestamp: Date.now(),
                        }));
                    },
                    undefined,
                    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
                );

                // Clean up watch on component unmount
                return () => navigator.geolocation.clearWatch(watchId);
            }
        } catch (error) {
            console.error('Error getting location:', error);
            // Keep existing coordinates if available
            setMetadata((prev) => ({
                ...prev,
                latitude: 0,
                longitude: 0,
                timestamp: Date.now(),
            }));

            // Show more specific error message
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Could not get location';
            setLocationError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsGettingLocation(false);
        }
    };

    useEffect(() => {
        loadOfflinePhotos();
        updateLocation();

        // Set up periodic location updates
        let locationInterval: NodeJS.Timeout | null = null;
        if (showCamera) {
            locationInterval = setInterval(updateLocation, 30000); // Update every 30 seconds
        }

        return () => {
            if (locationInterval) {
                clearInterval(locationInterval);
            }
        };
    }, [reportId]);

    const handleCapture = useCallback(async () => {
        if (!selectedTask || !webcamRef.current) return;

        // Check if we have valid location data
        if (metadata.latitude === 0 && metadata.longitude === 0) {
            toast.error(
                'Waiting for location data. Please wait a moment and try again.',
            );
            // Try to get location again
            await updateLocation();
            return;
        }

        let canvas: HTMLCanvasElement | null = null;
        let ctx: CanvasRenderingContext2D | null = null;

        try {
            // Get screenshot from webcam
            const imageSrc = webcamRef.current.getScreenshot();
            if (!imageSrc) {
                throw new Error('Failed to capture photo');
            }

            // Create the metadata object with current values
            const currentMetadata = {
                ...metadata,
                deviceInfo: {
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                    vendor: navigator.vendor,
                },
                timestamp: Date.now(),
            };

            // Create a new image
            const img = new Image();

            // Load the image and create canvas
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = imageSrc;
            });

            // Create canvas
            canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            // Get context
            ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Could not get canvas context');
            }

            // Draw image
            ctx.drawImage(img, 0, 0);

            // Add metadata overlay
            ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            ctx.fillRect(0, canvas.height - 60, canvas.width, 60);

            // Add timestamp and GPS info
            ctx.fillStyle = 'white';
            ctx.font = '20px Arial';
            const timestamp = format(new Date(), 'MMM d, yyyy HH:mm:ss');
            ctx.fillText(timestamp, 10, canvas.height - 35);

            // Add GPS coordinates
            if (
                currentMetadata.latitude !== 0 &&
                currentMetadata.longitude !== 0
            ) {
                ctx.font = '16px Arial';
                const coords = `${currentMetadata.latitude.toFixed(6)}, ${currentMetadata.longitude.toFixed(6)}`;
                ctx.fillText(coords, 10, canvas.height - 10);
            }

            // Convert to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to create blob'));
                        }
                    },
                    'image/jpeg',
                    0.9,
                );
            });

            // Create file and URL
            const file = new File([blob], `photo-${Date.now()}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now(),
            });
            const photoUrl = URL.createObjectURL(blob);

            // Update state
            setLastPhoto({ url: photoUrl, timestamp: Date.now() });
            setPhotos((prev) => [...prev, { url: photoUrl, file }]);

            // Store metadata with the photo
            if (!navigator.onLine) {
                const photoId = await saveOfflinePhoto(
                    reportId,
                    selectedTask,
                    file,
                    currentMetadata,
                );
                // Ensure metadata is properly structured for the backend
                await addPendingAction({
                    type: 'uploadPhoto',
                    data: {
                        reportId,
                        taskItem: selectedTask,
                        file,
                        metadata: currentMetadata,
                        photoId,
                    },
                });
            }

            // Flash animation
            const flash = document.getElementById('flash');
            if (flash) {
                flash.style.animation = 'none';
                // Force reflow
                void flash.offsetWidth;
                flash.style.animation = 'flash 0.5s';
            }

            toast.success('Photo captured successfully');

            // Clean up
            URL.revokeObjectURL(imageSrc);
            if (canvas) {
                canvas.remove();
            }
        } catch (error) {
            console.error('Error capturing photo:', error);
            toast.error(
                'Failed to capture photo: ' +
                    (error instanceof Error ? error.message : 'Unknown error'),
            );
            // Clean up on error
            if (canvas) {
                canvas.remove();
            }
        }
    }, [selectedTask, metadata, reportId]);

    const handleFlashToggle = async () => {
        if (!trackRef.current || !hasFlash) return;

        const getNextMode = (current: string) => {
            switch (current) {
                case 'off':
                    return 'on';
                case 'on':
                    return 'auto';
                case 'auto':
                    return 'off';
                default:
                    return 'off';
            }
        };

        const newMode = getNextMode(flashMode);
        setFlashMode(newMode);

        try {
            const track = trackRef.current;
            // Update metadata with device info
            setMetadata({
                ...metadata,
                timestamp: Date.now(),
                deviceInfo: {
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                    vendor: navigator.vendor,
                },
            });

            await track.applyConstraints({
                advanced: [{ torch: newMode === 'on' }],
            });
        } catch (error) {
            console.error('Failed to toggle flash:', error);
            toast.error('Failed to toggle flash');
        }
    };

    const handleCameraError = useCallback((error: string | DOMException) => {
        console.error('Camera error:', error);
        let errorMessage = 'Failed to access camera';

        if (error instanceof DOMException) {
            switch (error.name) {
                case 'NotAllowedError':
                    errorMessage =
                        'Camera access denied. Please enable camera permissions.';
                    break;
                case 'NotFoundError':
                    errorMessage = 'No camera found on your device.';
                    break;
                case 'NotReadableError':
                    errorMessage = 'Camera is in use by another application.';
                    break;
                case 'OverconstrainedError':
                    errorMessage =
                        'Could not find a suitable camera. Trying alternative...';
                    break;
                default:
                    errorMessage = error.message || 'Failed to access camera';
            }
        } else if (typeof error === 'string') {
            errorMessage = error;
        }

        setCameraError(errorMessage);
        toast.error(errorMessage);
    }, []);

    const handleUserMedia = () => {
        if (!isFullscreen) {
            enterFullscreen();
        }

        // Get the video track and check flash capability
        const stream = webcamRef.current?.stream;
        if (stream) {
            const track = stream.getVideoTracks()[0];
            trackRef.current = track;
            const capabilities = track.getCapabilities();
            setHasFlash(!!capabilities.torch);
        }

        setCameraError(null);
    };

    const enterFullscreen = async () => {
        try {
            const container = document.getElementById('camera-container');
            if (!container) return;

            if (container.requestFullscreen) {
                await container.requestFullscreen();
            } else if ((container as any).webkitRequestFullscreen) {
                await (container as any).webkitRequestFullscreen();
            } else if ((container as any).msRequestFullscreen) {
                await (container as any).msRequestFullscreen();
            }
            setIsFullscreen(true);

            if (screen.orientation && screen.orientation.lock) {
                try {
                    await screen.orientation.lock('landscape');
                } catch (error) {
                    console.log('Could not lock orientation:', error);
                }
            }
        } catch (error) {
            console.error('Error entering fullscreen:', error);
        }
    };

    const exitFullscreen = async () => {
        try {
            // Turn off flash if it's on
            if (trackRef.current && hasFlash) {
                try {
                    await trackRef.current.applyConstraints({
                        advanced: [{ torch: false }],
                    });
                } catch (error) {
                    console.error('Failed to turn off flash:', error);
                }
            }

            if (!document.fullscreenElement) {
                setIsFullscreen(false);
                return;
            }

            if (document.exitFullscreen) {
                await document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                await (document as any).webkitExitFullscreen();
            } else if ((document as any).msExitFullscreen) {
                await (document as any).msExitFullscreen();
            }

            setIsFullscreen(false);

            if (screen.orientation && screen.orientation.unlock) {
                screen.orientation.unlock();
            }
        } catch (error) {
            console.error('Error exiting fullscreen:', error);
            setIsFullscreen(false);
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        await updateLocation();

        try {
            const processedPhotos = await Promise.all(
                files.map(async (file) => {
                    const img = new Image();
                    const imageUrl = URL.createObjectURL(file);

                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = imageUrl;
                    });

                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    let width = img.width;
                    let height = img.height;
                    const maxSize = 1920;

                    if (width > height && width > maxSize) {
                        height = (height * maxSize) / width;
                        width = maxSize;
                    } else if (height > maxSize) {
                        width = (width * maxSize) / height;
                        height = maxSize;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx?.drawImage(img, 0, 0, width, height);

                    const blob = await new Promise<Blob>((resolve) => {
                        canvas.toBlob(
                            (blob) => resolve(blob as Blob),
                            'image/jpeg',
                            0.7,
                        );
                    });

                    URL.revokeObjectURL(imageUrl);

                    const compressedFile = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    });

                    return {
                        url: URL.createObjectURL(blob),
                        file: compressedFile,
                    };
                }),
            );

            setPhotos((prev) => [...prev, ...processedPhotos]);
        } catch (error) {
            console.error('Error processing photos:', error);
            toast.error('Failed to process photos. Please try again.');
        }
    };

    const handleRemovePhoto = (index: number) => {
        // Revoke the URL to prevent memory leaks
        URL.revokeObjectURL(photos[index].url);
        setPhotos((prev) => prev.filter((_, i) => i !== index));
        toast.success('Photo removed');
    };

    const handleUploadPhotos = async () => {
        if (!selectedTask || photos.length === 0) {
            toast.error('Missing required data for upload');
            return;
        }

        setLoading(true);
        try {
            if (!navigator.onLine) {
                await Promise.all(
                    photos.map(async (photo, index) => {
                        // Create unique metadata for each photo
                        const photoMetadata = {
                            latitude: metadata.latitude,
                            longitude: metadata.longitude,
                            accuracy: metadata.accuracy || 0,
                            timestamp: Date.now() + index * 1000, // Ensure unique timestamps
                            deviceInfo: {
                                userAgent: navigator.userAgent,
                                platform: navigator.platform,
                                vendor: navigator.vendor,
                            },
                        };
                        const photoId = await saveOfflinePhoto(
                            reportId,
                            selectedTask,
                            photo.file,
                            photoMetadata,
                        );
                        await addPendingAction({
                            type: 'uploadPhoto',
                            data: {
                                reportId,
                                taskItem: selectedTask,
                                file: photo.file,
                                metadata: photoMetadata,
                                photoId,
                            },
                        });
                    }),
                );
                await loadOfflinePhotos();
                toast.success(
                    'Photos saved offline. Will upload automatically when online.',
                );
                onBack();
                return;
            }

            // Create FormData with separate metadata for each photo
            const formData = new FormData();
            photos.forEach((photo, index) => {
                formData.append('photos', photo.file);
                formData.append('photoType', 'PROGRESS');
                // Create and append individual metadata for each photo
                const photoMetadata = {
                    latitude: metadata.latitude,
                    longitude: metadata.longitude,
                    accuracy: metadata.accuracy || 0,
                    timestamp: Date.now() + index * 1000, // Ensure unique timestamps
                    deviceInfo: {
                        userAgent: navigator.userAgent,
                        platform: navigator.platform,
                        vendor: navigator.vendor,
                    },
                };
                formData.append(
                    `metadata[${index}]`,
                    JSON.stringify(photoMetadata),
                );
            });
            formData.append('taskItem', selectedTask);

            const response = await fetch(`${API_BASE}/bid-photos/${reportId}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(
                    errorData?.message || 'Failed to upload photos',
                );
            }

            toast.success(
                `Successfully uploaded ${photos.length} photo${photos.length !== 1 ? 's' : ''}`,
            );
            onBack();
        } catch (error) {
            console.error(
                'Photo upload error:',
                error instanceof Error ? error.message : 'Unknown error',
            );
            if (!navigator.onLine) {
                try {
                    await Promise.all(
                        photos.map(async (photo, index) => {
                            const photoId = await saveOfflinePhoto(
                                reportId,
                                selectedTask,
                                photo.file,
                                metadata,
                            );
                            await addPendingAction({
                                type: 'uploadPhoto',
                                data: {
                                    reportId,
                                    taskItem: selectedTask,
                                    file: photo.file,
                                    metadata,
                                    photoId,
                                },
                            });
                        }),
                    );
                    await loadOfflinePhotos();
                    toast.success(
                        'Photos saved offline. Will upload automatically when online.',
                    );
                    onBack();
                } catch (offlineError) {
                    console.error(
                        'Failed to save photos offline:',
                        offlineError,
                    );
                    toast.error(
                        'Failed to save photos offline. Please try again.',
                    );
                }
            } else {
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to upload photos. Please try again.',
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        selectedTask,
        setSelectedTask,
        loading,
        photos,
        offlinePhotos,
        showCamera,
        setShowCamera,
        facingMode,
        isFullscreen,
        setIsFullscreen,
        flashMode,
        hasFlash,
        cameraError,
        lastPhoto,
        fileInputRef,
        webcamRef,
        handleCapture,
        handleFlashToggle,
        handleFileSelect,
        handleUploadPhotos,
        handleRemovePhoto,
        handleUserMedia,
        exitFullscreen,
        loadOfflinePhotos,
        isGettingLocation,
        locationError,
        metadata,
    };
}
